import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Eye, EyeOff, Building2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useAuthWithRetry } from '@/hooks/useAuthWithRetry';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
const loginSchema = z.object({
  email: z.string().min(1, 'E-mail é obrigatório').refine(email => {
    // Validação mais flexível de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, 'E-mail inválido'),
  password: z.string().min(1, 'Senha é obrigatória')
});
const signupSchema = z.object({
  email: z.string().min(1, 'E-mail é obrigatório').refine(email => {
    // Validação mais flexível de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, 'E-mail inválido'),
  password: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres').regex(/^(?=.*[a-zA-Z])(?=.*\d)/, 'Senha deve conter pelo menos 1 letra e 1 número'),
  confirmPassword: z.string(),
  fullName: z.string().min(2, 'Nome completo é obrigatório'),
  organizacao: z.string().optional()
}).refine(data => data.password === data.confirmPassword, {
  message: "Senhas não coincidem",
  path: ["confirmPassword"]
});
const resetSchema = z.object({
  email: z.string().min(1, 'E-mail é obrigatório').refine(email => {
    // Validação mais flexível de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, 'E-mail inválido')
});
type LoginForm = z.infer<typeof loginSchema>;
type SignupForm = z.infer<typeof signupSchema>;
type ResetForm = z.infer<typeof resetSchema>;
export default function Auth() {
  const [mode, setMode] = useState<'login' | 'signup' | 'reset' | 'email-confirmation' | 'pending-approval'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [lastResetAttempt, setLastResetAttempt] = useState<number>(0);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [showPasswordRecoverySuggestion, setShowPasswordRecoverySuggestion] = useState(false);
  const {
    signIn,
    resetPassword
  } = useAuth();
  const { signUpWithRetry, isLoading: retryLoading } = useAuthWithRetry();
  const {
    toast
  } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Usar o hook de redirecionamento para autenticar após login
  useAuthRedirect();
  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema)
  });
  const signupForm = useForm<SignupForm>({
    resolver: zodResolver(signupSchema)
  });
  const resetForm = useForm<ResetForm>({
    resolver: zodResolver(resetSchema)
  });
  const handleRedirectAfterLogin = async (email: string) => {
    console.log('🔄 Redirecionando usuário após login:', email);
    try {
      // Check if user is in hiring process
      const {
        data: contratacaoData
      } = await supabase.from('contratacao').select('email').eq('email', email).maybeSingle();
      if (contratacaoData) {
        console.log('✅ Usuário em processo de contratação, redirecionando para /contratacao');
        navigate('/contratacao');
        return;
      }

      // User not in hiring process - redirect to no organization page
      console.log('⚠️ Usuário sem vinculação organizacional, redirecionando para /no-organization');
      navigate('/no-organization');
    } catch (error) {
      console.error('❌ Erro na lógica de redirecionamento:', error);
      // If there's an error, redirect to no-org page
      navigate('/no-organization');
    }
  };
  const onLogin = async (data: LoginForm) => {
    console.log('🔐 Tentando fazer login com:', data.email);
    const {
      error
    } = await signIn(data.email, data.password);
    if (error) {
      console.error('❌ Erro no login:', error);
      
      // Incrementar contador de tentativas
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      // Verificar se é erro de email não confirmado
      if (error.message.includes('email_not_confirmed') || error.message.includes('not confirmed')) {
        toast({
          title: "E-mail não confirmado",
          description: "Sua conta ainda não foi confirmada. Verifique seu e-mail para continuar.",
          variant: "destructive"
        });
      } else if (error.message.includes('Invalid login credentials')) {
        // Após 2 tentativas falhas, sugerir recuperação de senha
        if (newAttempts >= 2) {
          setShowPasswordRecoverySuggestion(true);
          toast({
            title: "Credenciais inválidas",
            description: "Você errou a senha várias vezes. Considere recuperar sua senha caso tenha esquecido.",
            variant: "destructive",
            duration: 8000,
          });
        } else {
          toast({
            title: "Erro no login",
            description: "E-mail ou senha incorretos. Verifique suas credenciais.",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Erro no login",
          description: error.message,
          variant: "destructive"
        });
      }
      return;
    }
    
    // Reset tentativas após login bem sucedido
    setLoginAttempts(0);
    setShowPasswordRecoverySuggestion(false);

    // Verificar se o email foi confirmado
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (user && !user.email_confirmed_at) {
      await supabase.auth.signOut();
      toast({
        title: "E-mail não confirmado",
        description: "Sua conta ainda não foi confirmada. Verifique seu e-mail para continuar.",
        variant: "destructive"
      });
      return;
    }
    console.log('✅ Login realizado com sucesso');
    toast({
      title: "Login realizado",
      description: "Bem-vindo à Intranet Grupo Ares!"
    });

    // O useAuthRedirect hook irá tratar o redirecionamento automaticamente
  };
  const onSignup = async (data: SignupForm) => {
    try {
      // Se organização foi fornecida, verificar se existe
      let organizacao = null;
      if (data.organizacao && data.organizacao.trim()) {
        console.log('🔍 Procurando organização:', data.organizacao.trim());
        
        // Tentar busca exata primeiro
        let { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('id, name')
          .ilike('name', data.organizacao.trim())
          .maybeSingle();
        
        // Se não encontrou com busca exata, tentar busca parcial
        if (!orgData && !orgError) {
          console.log('🔍 Busca exata não encontrou, tentando busca parcial...');
          const { data: orgDataPartial, error: orgErrorPartial } = await supabase
            .from('organizations')
            .select('id, name')
            .ilike('name', `%${data.organizacao.trim()}%`)
            .maybeSingle();
          
          orgData = orgDataPartial;
          orgError = orgErrorPartial;
        }
        
        console.log('📊 Resultado da busca:', { orgData, orgError });

        if (orgError) {
          console.error('Erro ao verificar organização:', orgError);
          toast({
            title: "Erro",
            description: "Erro ao verificar organização. Tente novamente.",
            variant: "destructive"
          });
          return;
        }

        if (!orgData) {
          console.log('⚠️ Organização não encontrada:', data.organizacao.trim());
          
          // Buscar organizações similares para sugestão
          const { data: similarOrgs } = await supabase
            .from('organizations')
            .select('name')
            .ilike('name', `%${data.organizacao.trim().split(' ')[0]}%`)
            .limit(3);
          
          const sugestoes = similarOrgs?.map(org => org.name).join(', ') || '';
          const mensagem = sugestoes 
            ? `Não encontramos a organização "${data.organizacao.trim()}". Organizações disponíveis: ${sugestoes}`
            : `Não encontramos a organização "${data.organizacao.trim()}". Digite o nome exato da organização.`;
          
          toast({
            title: "Organização não encontrada",
            description: mensagem,
            variant: "destructive"
          });
          return;
        }

        organizacao = orgData;
      }

      const {
        data: signupData,
        error
      } = await signUpWithRetry(data.email, data.password, { full_name: data.fullName });
      
      if (error) {
        if (error.message.includes('already registered')) {
          toast({
            title: "Usuário já existe",
            description: "Este e-mail já está cadastrado. Tente fazer login.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Erro no cadastro",
            description: error.message,
            variant: "destructive"
          });
        }
        return;
      }

      // Fazer logout imediato caso o Supabase tenha logado automaticamente
      await supabase.auth.signOut();

      // Se organização existe, criar solicitação de acesso
      if (organizacao) {
        console.log('✅ Criando solicitação para organização:', organizacao.name);
        
        const { error: requestError } = await supabase
          .from('organization_requests')
          .insert({
            email: data.email,
            organization_id: organizacao.id,
            organization_name: organizacao.name,
            reason: `Solicitação de acesso à organização: ${organizacao.name}`
            // status fica como NULL para indicar pending
          });
        
        console.log('📝 Resultado da criação da solicitação:', { requestError });

        if (requestError) {
          console.error('Erro ao criar solicitação de acesso:', requestError);
          toast({
            title: "Erro",
            description: "Erro ao criar solicitação de acesso. Tente novamente.",
            variant: "destructive"
          });
          return;
        }

        console.log('🎉 Solicitação criada com sucesso!');
        setRegisteredEmail(data.email);
        
        // SEMPRE redirecionar para confirmação de email primeiro, independente da organização
        toast({
          title: "Cadastro realizado!",
          description: "Enviamos um e-mail de verificação. Confirme seu endereço de e-mail para ativar sua conta."
        });
        
        // Redirecionar para a página de confirmação de email
        navigate('/email-confirmation?email=' + encodeURIComponent(data.email));
      } else {
        // Redirecionar para tela de confirmação de email
        toast({
          title: "Cadastro realizado!",
          description: "Enviamos um e-mail de verificação. Confirme seu endereço de e-mail para ativar sua conta."
        });
        
        // Redirecionar para a página de confirmação de email
        navigate('/email-confirmation?email=' + encodeURIComponent(data.email));
      }
    } catch (error) {
      console.error('Erro inesperado no cadastro:', error);
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro durante o cadastro. Tente novamente.",
        variant: "destructive"
      });
    }
  };
  const onReset = async (data: ResetForm) => {
    // Throttling: evitar múltiplas tentativas rápidas (rate limiting)
    const now = Date.now();
    const timeSinceLastAttempt = now - lastResetAttempt;
    const minimumInterval = 60000; // 1 minuto

    if (timeSinceLastAttempt < minimumInterval) {
      const waitTime = Math.ceil((minimumInterval - timeSinceLastAttempt) / 1000);
      toast({
        title: "Aguarde antes de tentar novamente",
        description: `Você pode tentar novamente em ${waitTime} segundos para evitar bloqueio.`,
        variant: "destructive"
      });
      return;
    }
    setIsResetLoading(true);
    setLastResetAttempt(now);
    try {
      const {
        error
      } = await resetPassword(data.email);
      if (error) {
        console.error('Erro no reset password:', error);

        // Tratamento específico para rate limiting
        if (error.message?.includes('rate_limit') || error.message?.includes('429')) {
          toast({
            title: "Muitas tentativas",
            description: "Você fez muitas tentativas recentes. Aguarde alguns minutos antes de tentar novamente.",
            variant: "destructive"
          });
        } else if (error.message?.includes('email_not_confirmed')) {
          toast({
            title: "E-mail não confirmado",
            description: "Este e-mail ainda não foi confirmado. Verifique sua caixa de entrada primeiro.",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Erro na recuperação",
            description: error.message || "Ocorreu um erro ao enviar o e-mail de recuperação.",
            variant: "destructive"
          });
        }
        return;
      }
      toast({
        title: "E-mail enviado com sucesso!",
        description: `Enviamos um link de recuperação para ${data.email}. Verifique sua caixa de entrada e pasta de spam.`
      });

      // Limpar o formulário
      resetForm.reset();
      setMode('login');
    } catch (err) {
      console.error('Erro inesperado:', err);
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro inesperado. Tente novamente mais tarde.",
        variant: "destructive"
      });
    } finally {
      setIsResetLoading(false);
    }
  };

  // Detectar quando usuário retorna da verificação de email ou modo reset
  useEffect(() => {
    const verified = searchParams.get('verified');
    const resetMode = searchParams.get('mode');
    if (verified === 'true') {
      toast({
        title: "E-mail verificado!",
        description: "Sua conta foi ativada com sucesso. Agora você pode fazer login."
      });
      // Limpar o parâmetro da URL
      navigate('/auth', {
        replace: true
      });
    }
    if (resetMode === 'reset') {
      setMode('reset');
      // Limpar o parâmetro da URL
      navigate('/auth', {
        replace: true
      });
    }
  }, [searchParams, navigate, toast]);
  return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-secondary/20 p-4">
      {/* Botão de voltar para página inicial */}
      <Button 
        variant="ghost" 
        size="sm"
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 flex items-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar para início
      </Button>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            {mode === 'login' && 'Lyra RH'}
            {mode === 'signup' && 'Criar Conta'}
            {mode === 'reset' && 'Recuperar Senha'}
            {mode === 'email-confirmation' && 'Confirme seu E-mail'}
            {mode === 'pending-approval' && 'Aguardando Aprovação'}
          </CardTitle>
          
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === 'login' && <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" placeholder="seu@email.com" {...loginForm.register('email')} />
                {loginForm.formState.errors.email && <p className="text-sm text-destructive">{loginForm.formState.errors.email.message}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Digite sua senha" {...loginForm.register('password')} />
                  <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {loginForm.formState.errors.password && <p className="text-sm text-destructive">{loginForm.formState.errors.password.message}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={loginForm.formState.isSubmitting}>
                {loginForm.formState.isSubmitting ? 'Entrando...' : 'Entrar'}
              </Button>

              {/* Sugestão de recuperação de senha após múltiplas tentativas */}
              {showPasswordRecoverySuggestion && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/50 rounded-lg">
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-medium mb-2">
                    ⚠️ Você está tendo problemas para entrar?
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Se você clicou em um link de recuperação de senha mas não definiu uma nova senha, 
                    sua conta pode estar sem senha válida. Clique abaixo para solicitar um novo link.
                  </p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    className="w-full border-amber-500 text-amber-600 hover:bg-amber-500/10"
                    onClick={() => {
                      setMode('reset');
                      // Preencher automaticamente o email se disponível
                      const currentEmail = loginForm.getValues('email');
                      if (currentEmail) {
                        resetForm.setValue('email', currentEmail);
                      }
                    }}
                  >
                    Recuperar minha senha
                  </Button>
                </div>
              )}

              <div className="text-center space-y-2">
                <Button type="button" variant="link" onClick={() => setMode('signup')} className="text-sm">
                  Criar conta
                </Button>
                <Button type="button" variant="link" onClick={() => setMode('reset')} className="text-sm">
                  Recuperar senha
                </Button>
              </div>
            </form>}

          {mode === 'signup' && <form onSubmit={signupForm.handleSubmit(onSignup)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Nome Completo</Label>
                <Input id="fullName" placeholder="Seu nome completo" {...signupForm.register('fullName')} />
                {signupForm.formState.errors.fullName && <p className="text-sm text-destructive">{signupForm.formState.errors.fullName.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" placeholder="seu@email.com" {...signupForm.register('email')} />
                {signupForm.formState.errors.email && <p className="text-sm text-destructive">{signupForm.formState.errors.email.message}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Mínimo 8 caracteres com letra e número" {...signupForm.register('password')} />
                  <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {signupForm.formState.errors.password && <p className="text-sm text-destructive">{signupForm.formState.errors.password.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                <div className="relative">
                  <Input id="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder="Confirme sua senha" {...signupForm.register('confirmPassword')} />
                  <Button type="button" variant="ghost" size="sm" className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {signupForm.formState.errors.confirmPassword && <p className="text-sm text-destructive">{signupForm.formState.errors.confirmPassword.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="organizacao">Organização <span className="text-muted-foreground">(opcional)</span></Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    id="organizacao" 
                    placeholder="Nome da sua organização" 
                    className="pl-10"
                    {...signupForm.register('organizacao')} 
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Se você trabalha para uma organização específica, insira o nome aqui. Aguardará aprovação do administrador.
                </p>
                {signupForm.formState.errors.organizacao && <p className="text-sm text-destructive">{signupForm.formState.errors.organizacao.message}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={signupForm.formState.isSubmitting}>
                {signupForm.formState.isSubmitting ? 'Criando...' : 'Criar conta'}
              </Button>

              <div className="text-center">
                <Button type="button" variant="link" onClick={() => setMode('login')} className="text-sm">
                  Já tem conta? Fazer login
                </Button>
              </div>
            </form>}

          {mode === 'reset' && <form onSubmit={resetForm.handleSubmit(onReset)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" placeholder="seu@email.com" {...resetForm.register('email')} />
                {resetForm.formState.errors.email && <p className="text-sm text-destructive">{resetForm.formState.errors.email.message}</p>}
              </div>

              <Button type="submit" className="w-full" disabled={resetForm.formState.isSubmitting || isResetLoading}>
                {resetForm.formState.isSubmitting || isResetLoading ? 'Enviando...' : 'Recuperar senha'}
              </Button>
              
              {lastResetAttempt > 0 && <div className="text-center">
                  <p className="text-xs text-muted-foreground">
                    Aguarde 1 minuto entre tentativas para evitar bloqueio
                  </p>
                </div>}

              <div className="text-center">
                <Button type="button" variant="link" onClick={() => setMode('login')} className="text-sm">
                  Voltar ao login
                </Button>
                
                {/* Debug helper */}
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-muted-foreground mb-2">
                    Problemas com o link de reset?
                  </p>
                  <a href="/debug-reset" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                    Abrir ferramenta de diagnóstico
                  </a>
                </div>
              </div>
            </form>}

          {mode === 'pending-approval' && <div className="space-y-4 text-center">
              <div className="p-6 bg-orange-50 rounded-lg border border-orange-200">
                <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-orange-900 mb-2">
                  Aguardando aprovação da organização
                </h3>
                <p className="text-orange-800 mb-4">
                  Sua conta foi criada com sucesso! Enviamos uma solicitação de acesso para os administradores da organização.
                </p>
                <p className="text-sm text-orange-700 mb-6">
                  Você receberá um e-mail quando sua solicitação for aprovada. Enquanto isso, você pode:
                </p>
                <ul className="text-sm text-orange-700 text-left space-y-2 mb-6">
                  <li>• Aguardar a aprovação do administrador</li>
                  <li>• Entrar em contato diretamente com a organização</li>
                  <li>• Verificar seu e-mail para confirmação da conta</li>
                </ul>
              </div>

              <div className="text-center space-y-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setMode('login')} 
                  className="w-full"
                >
                  Voltar ao login
                </Button>
                <p className="text-xs text-muted-foreground">
                  E-mail cadastrado: {registeredEmail}
                </p>
              </div>
            </div>}

          {mode === 'email-confirmation' && <div className="space-y-4 text-center">
              <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  E-mail de confirmação enviado!
                </h3>
                <p className="text-blue-800 mb-4">
                  Enviamos um e-mail de verificação para:
                </p>
                <p className="font-medium text-blue-900 mb-4">
                  {registeredEmail}
                </p>
                <p className="text-sm text-blue-700">
                  Confirme seu endereço de e-mail para ativar sua conta. Sua conta só será ativada após a confirmação.
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Não recebeu o e-mail? Verifique sua pasta de spam.
                </p>
                
                <Button type="button" variant="outline" className="w-full" onClick={async () => {
              // Usar a URL de produção configurada
              const baseUrl = window.location.hostname === 'localhost' ? window.location.origin : 'https://ateris.com.br';
              const redirectUrl = `${baseUrl}/auth?verified=true`;
              const {
                error
              } = await supabase.auth.resend({
                type: 'signup',
                email: registeredEmail,
                options: {
                  emailRedirectTo: redirectUrl
                }
              });
              if (error) {
                toast({
                  title: "Erro",
                  description: "Erro ao reenviar e-mail. Tente novamente.",
                  variant: "destructive"
                });
              } else {
                toast({
                  title: "E-mail reenviado",
                  description: "Verifique sua caixa de entrada novamente."
                });
              }
            }}>
                  Reenviar e-mail de confirmação
                </Button>

                <Button type="button" variant="link" onClick={() => setMode('login')} className="text-sm">
                  Voltar ao login
                </Button>
              </div>
            </div>}
        </CardContent>
      </Card>
    </div>;
}